import { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { TrendingUp, TrendingDown } from "lucide-react";

const COINS = [
  { key: "bitcoin", symbol: "BTC" },
  { key: "ethereum", symbol: "ETH" },
  { key: "solana", symbol: "SOL" },
  { key: "ripple", symbol: "XRP" },
];

const RANGES = [
  { key: 1, label: "24H" },
  { key: 7, label: "7D" },
  { key: 30, label: "30D" },
];

const fmtEur = (n) =>
  `€${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtEurCompact = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `€${(v / 1000).toFixed(1)}k`;
  return `€${v.toFixed(v < 1 ? 3 : 0)}`;
};

const fmtTick = (t, days) => {
  const d = new Date(t);
  if (days <= 1) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

// Slice a longer history payload down to the user-selected window. The
// backend returns whatever Coinbase supplies at its chosen granularity, so we
// filter client-side by timestamp so the chart always reflects the range tab.
const sliceRange = (points, days) => {
  if (!points || points.length === 0) return [];
  const cutoff = Date.now() - days * 86400000;
  return points.filter((p) => p.t >= cutoff);
};

export default function PriceChart({ prices: pricesProp }) {
  const [coin, setCoin] = useState("bitcoin");
  const [range, setRange] = useState(7);
  const [history, setHistory] = useState({}); // key: `${coin}:${days}` → payload
  const [loading, setLoading] = useState(false);
  const [ownPrices, setOwnPrices] = useState(null);

  // If the parent hasn't supplied the price map (e.g. on the public landing
  // page), fetch it ourselves so the header still shows the current price and
  // 24h change.
  useEffect(() => {
    if (pricesProp) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/crypto/prices");
        if (!cancelled) setOwnPrices(data || {});
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pricesProp]);

  const prices = pricesProp || ownPrices;
  const cacheKey = `${coin}:${range}`;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (history[cacheKey]) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/crypto/history`, {
          params: { coin, days: range },
        });
        if (!cancelled) {
          setHistory((h) => ({ ...h, [cacheKey]: data?.points || [] }));
        }
      } catch {
        if (!cancelled) setHistory((h) => ({ ...h, [cacheKey]: [] }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [cacheKey, coin, range, history]);

  const data = useMemo(() => {
    const raw = history[cacheKey] || [];
    return sliceRange(raw, range);
  }, [history, cacheKey, range]);

  const change24h = prices?.[coin]?.eur_24h_change;
  const currentPrice = prices?.[coin]?.eur;
  const meta = COINS.find((c) => c.key === coin) || COINS[0];

  const first = data[0]?.p;
  const last = data[data.length - 1]?.p;
  const rangeChangePct =
    first && last && first > 0 ? ((last - first) / first) * 100 : null;

  const positive = (rangeChangePct ?? change24h ?? 0) >= 0;
  const strokeColor = positive ? "#10B981" : "#F23F42";

  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-6" data-testid="price-chart">
      {/* HEADER: title + coin tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-xs font-mono uppercase tracking-widest text-white/50">
          Live Market Chart
        </p>
        <div className="flex flex-wrap gap-1.5">
          {COINS.map((c) => {
            const active = coin === c.key;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`chart-coin-${c.symbol.toLowerCase()}`}
                onClick={() => setCoin(c.key)}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                  active
                    ? "bg-[#FFD700] text-black border-[#FFD700]"
                    : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {c.symbol}
              </button>
            );
          })}
        </div>
      </div>

      {/* PRICE + CHANGE */}
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-xs font-mono text-white/40 uppercase tracking-widest">
            {meta.symbol} · EUR
          </div>
          <div className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">
            {currentPrice ? fmtEur(currentPrice) : "—"}
          </div>
          {change24h != null && (
            <div
              className={`inline-flex items-center gap-1 mt-1 text-xs font-mono ${
                change24h >= 0 ? "text-[#10B981]" : "text-[#F23F42]"
              }`}
            >
              {change24h >= 0 ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              {change24h >= 0 ? "+" : ""}
              {change24h.toFixed(2)}% · 24h
            </div>
          )}
        </div>
        <div className="flex gap-1.5">
          {RANGES.map((r) => {
            const active = range === r.key;
            return (
              <button
                key={r.key}
                type="button"
                data-testid={`chart-range-${r.label.toLowerCase()}`}
                onClick={() => setRange(r.key)}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                  active
                    ? "bg-white text-black border-white"
                    : "border-white/20 text-white/60 hover:text-white hover:border-white/40"
                }`}
              >
                {r.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* CHART */}
      <div className="h-56 w-full -mx-2" data-testid="price-chart-svg">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/30 text-xs font-mono uppercase tracking-widest">
            {loading ? "Loading chart…" : "No data"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="chart-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={strokeColor} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="t"
                tickFormatter={(t) => fmtTick(t, range)}
                axisLine={false}
                tickLine={false}
                minTickGap={40}
                stroke="rgba(255,255,255,0.35)"
                style={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
              />
              <YAxis
                dataKey="p"
                domain={["auto", "auto"]}
                tickFormatter={fmtEurCompact}
                axisLine={false}
                tickLine={false}
                width={48}
                stroke="rgba(255,255,255,0.35)"
                style={{ fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
              />
              <Tooltip
                cursor={{ stroke: "rgba(255,215,0,0.4)", strokeWidth: 1 }}
                contentStyle={{
                  background: "#000",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 0,
                  fontFamily: "JetBrains Mono, monospace",
                  fontSize: 12,
                  padding: "8px 10px",
                }}
                labelFormatter={(t) => new Date(t).toLocaleString()}
                formatter={(v) => [fmtEur(v), meta.symbol]}
              />
              <Area
                type="monotone"
                dataKey="p"
                stroke={strokeColor}
                strokeWidth={2}
                fill="url(#chart-fill)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* FOOTER STATS */}
      <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40">
        <span>Range · {RANGES.find((r) => r.key === range)?.label}</span>
        {rangeChangePct != null && (
          <span
            className={rangeChangePct >= 0 ? "text-[#10B981]" : "text-[#F23F42]"}
            data-testid="chart-range-change"
          >
            {rangeChangePct >= 0 ? "+" : ""}
            {rangeChangePct.toFixed(2)}%
          </span>
        )}
      </div>
    </div>
  );
}
